import * as React from "react";
import { cn } from "@/lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "@/components/icons";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  maxVisiblePages?: number;
  className?: string;
  isLoading?: boolean;
}

interface PageButtonProps {
  page: number;
  isActive: boolean;
  onClick: () => void;
}

function PageButton({ page, isActive, onClick }: PageButtonProps) {
  const [isPressed, setIsPressed] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => {
        setIsPressed(false);
        setIsHovered(false);
      }}
      onMouseEnter={() => setIsHovered(true)}
      className={cn(
        "relative size-8 rounded-full overflow-hidden shrink-0",
        "flex items-center justify-center",
        "typo-body-2-medium transition-colors",
        isActive ? "text-primary-strong" : "text-labels-primary",
      )}
    >
      <span className="relative z-10">{page}</span>
      {/* Overlay for states */}
      {isActive && (
        <div className="absolute inset-0 bg-primary-normal opacity-[0.06] pointer-events-none" />
      )}
      {!isActive && isHovered && !isPressed && (
        <div className="absolute inset-0 bg-static-black opacity-[0.02] pointer-events-none" />
      )}
      {!isActive && isPressed && (
        <div className="absolute inset-0 bg-static-black opacity-10 pointer-events-none" />
      )}
    </button>
  );
}

interface NavButtonProps {
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
  "aria-label": string;
}

function NavButton({
  onClick,
  disabled,
  children,
  "aria-label": ariaLabel,
}: NavButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "size-6 shrink-0 flex items-center justify-center",
        "transition-colors",
        disabled
          ? "text-labels-assistive cursor-not-allowed"
          : "text-labels-primary hover:text-labels-strong",
      )}
    >
      {children}
    </button>
  );
}

function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  maxVisiblePages = 7,
  isLoading = false,
  className,
}: PaginationProps) {
  // 로딩 중일 때는 1페이지만 표시
  const effectiveTotalPages = isLoading ? 1 : totalPages;
  const effectiveCurrentPage = isLoading ? 1 : currentPage;

  // Calculate visible page numbers
  const getVisiblePages = React.useCallback(() => {
    if (effectiveTotalPages <= maxVisiblePages) {
      return Array.from({ length: effectiveTotalPages }, (_, i) => i + 1);
    }

    const halfVisible = Math.floor(maxVisiblePages / 2);
    let start = effectiveCurrentPage - halfVisible;
    let end = effectiveCurrentPage + halfVisible;

    if (start < 1) {
      start = 1;
      end = maxVisiblePages;
    }

    if (end > effectiveTotalPages) {
      end = effectiveTotalPages;
      start = effectiveTotalPages - maxVisiblePages + 1;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [effectiveCurrentPage, effectiveTotalPages, maxVisiblePages]);

  const visiblePages = getVisiblePages();

  const goToFirstPage = () => onPageChange(1);
  const goToPreviousPage = () =>
    onPageChange(Math.max(1, effectiveCurrentPage - 1));
  const goToNextPage = () =>
    onPageChange(Math.min(effectiveTotalPages, effectiveCurrentPage + 1));
  const goToLastPage = () => onPageChange(effectiveTotalPages);

  return (
    <nav
      role="navigation"
      aria-label="페이지 네비게이션"
      className={cn("flex items-center gap-3", className)}
    >
      <NavButton
        onClick={goToFirstPage}
        disabled={isLoading || effectiveCurrentPage === 1}
        aria-label="첫 페이지로 이동"
      >
        <ChevronsLeft size={24} />
      </NavButton>

      <NavButton
        onClick={goToPreviousPage}
        disabled={isLoading || effectiveCurrentPage === 1}
        aria-label="이전 페이지로 이동"
      >
        <ChevronLeft size={24} />
      </NavButton>

      {visiblePages.length > 0 ? (
        visiblePages.map((page) => (
          <PageButton
            key={page}
            page={page}
            isActive={page === effectiveCurrentPage}
            onClick={() => onPageChange(page)}
          />
        ))
      ) : (
        <PageButton
          key={"no result"}
          page={1}
          isActive={true}
          onClick={() => null}
        />
      )}

      <NavButton
        onClick={goToNextPage}
        disabled={
          isLoading ||
          effectiveCurrentPage === effectiveTotalPages ||
          visiblePages.length === 0
        }
        aria-label="다음 페이지로 이동"
      >
        <ChevronRight size={24} />
      </NavButton>

      <NavButton
        onClick={goToLastPage}
        disabled={
          isLoading ||
          effectiveCurrentPage === effectiveTotalPages ||
          visiblePages.length === 0
        }
        aria-label="마지막 페이지로 이동"
      >
        <ChevronsRight size={24} />
      </NavButton>
    </nav>
  );
}

export { Pagination };
export type { PaginationProps };
